import { useParams, useNavigate, Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4";

const RARITY_COLORS = {
  common: 'var(--tier-common)',
  uncommon: 'var(--tier-uncommon)',
  rare: 'var(--tier-rare)',
  epic: 'var(--tier-epic)',
  legendary: 'var(--tier-legendary)',
};

const SLOT_LABEL = {
  lmb: 'Left Click',
  rmb: 'Right Click',
  f: 'F Key',
  ult: 'Ultimate · Q',
  passive: 'Passive',
};

function AbilityCard({ ability, color }) {
  const isUlt = ability.slot === 'ult';
  return (
    <div className="wiki-detail-box" style={{
      padding: '1rem 1.15rem',
      borderColor: isUlt ? `${color}55` : undefined,
      background: isUlt ? `linear-gradient(135deg, transparent 60%, ${color}0d 100%)` : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '0.6rem', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase',
          color: isUlt ? color : 'var(--text-muted)',
          background: isUlt ? `${color}18` : 'var(--bg-raised)',
          border: `1px solid ${isUlt ? `${color}44` : 'var(--border-mid)'}`,
          borderRadius: '2rem', padding: '3px 10px',
        }}>
          {SLOT_LABEL[ability.slot] ?? ability.slot}
        </span>
        <span style={{ fontWeight: 800, fontSize: '0.95rem', color: isUlt ? color : 'var(--text-primary)' }}>
          {ability.name}
        </span>
        {ability.cooldown && (
          <span style={{
            marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0,
          }}>
            <McIcon materialKey="clock" size={13} /> {ability.cooldown}
          </span>
        )}
      </div>
      {ability.description && (
        <p style={{ margin: 0, fontSize: '0.84rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
          {ability.description}
        </p>
      )}
    </div>
  );
}

export default function WikiRootDetail({ roots }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const all = (roots || []).filter(r => Number(r.enabled) !== 0);
  const root = all.find(r => r.slug === slug);

  if (!root) {
    return (
      <div className="wiki-detail-root" style={{ paddingTop: '8rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        Root not found. <Link to="/wiki/roots" style={{ color: 'var(--teal)' }}>← Back to all Roots</Link>
      </div>
    );
  }

  const rarityColor = RARITY_COLORS[root.rarity] ?? 'var(--tier-common)';
  const abilities = root.abilities || [];
  const actives = abilities.filter(a => a.slot !== 'passive');
  const passives = abilities.filter(a => a.slot === 'passive');
  const isStub = root.status === 'stub';
  const isWip = root.status === 'wip';

  const idx = all.findIndex(r => r.slug === slug);
  const prev = all[(idx - 1 + all.length) % all.length];
  const next = all[(idx + 1) % all.length];

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => navigate('/wiki/roots')}>← All Roots</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': root.color }}>
              <McIcon materialKey={root.material} size={56} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{
                background: `linear-gradient(90deg, ${root.color}, ${root.gradient_end || root.color})`,
                WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {root.name}
              </h1>
              <div className="wiki-detail-item-type" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ color: rarityColor, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                  {root.rarity}
                </span>
                {root.playstyle && (
                  <>
                    <span style={{ color: 'var(--text-muted)' }}>·</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{root.playstyle}</span>
                  </>
                )}
                {(isStub || isWip) && (
                  <span style={{
                    fontSize: '0.62rem', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
                    color: '#c8b800', border: '1px solid rgba(200,184,0,0.4)', background: 'rgba(200,184,0,0.1)',
                    borderRadius: '2rem', padding: '2px 9px',
                  }}>
                    {isWip ? 'Being reworked' : 'In development'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">

          {/* ── Main ── */}
          <StaggerItem className="wiki-detail-main">
            {root.description && (
              <div>
                <span className="wiki-detail-label">About</span>
                <div className="wiki-detail-box" style={{ padding: '1.1rem 1.25rem' }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                    {root.description}
                  </p>
                </div>
              </div>
            )}

            <div>
              <span className="wiki-detail-label">Abilities</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {actives.map(a => <AbilityCard key={a.id} ability={a} color={root.color} />)}
              </div>
            </div>

            {passives.length > 0 && (
              <div>
                <span className="wiki-detail-label">Passives</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {passives.map(a => <AbilityCard key={a.id} ability={a} color={root.color} />)}
                </div>
              </div>
            )}
          </StaggerItem>

          {/* ── Sidebar ── */}
          <StaggerItem className="wiki-detail-sidebar">
            <div>
              <span className="wiki-detail-label">Quick Facts</span>
              <div className="wiki-detail-box" style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', fontSize: '0.84rem' }}>
                {[
                  ['Rarity', <span style={{ color: rarityColor, fontWeight: 700, textTransform: 'capitalize' }}>{root.rarity}</span>],
                  ['Playstyle', root.playstyle || '—'],
                  ['Tiers', 'I · II · III'],
                  ['Active abilities', actives.length],
                  ['Passives', passives.length || '—'],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="wiki-detail-label">Tier Scaling</span>
              <div className="wiki-detail-box" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                Upgrading your root boosts damage, extends durations and widens areas of effect.
                Tier III unlocks the root's full strength.
              </div>
            </div>

            <div>
              <span className="wiki-detail-label">More Roots</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[prev, next].filter((r, i, arr) => r && r.slug !== root.slug && arr.findIndex(x => x.slug === r.slug) === i).map(r => (
                  <Link key={r.slug} to={`/wiki/root/${r.slug}`} style={{ textDecoration: 'none' }}>
                    <div className="wiki-detail-box" style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.7rem 0.9rem' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = r.color}
                      onMouseLeave={e => e.currentTarget.style.borderColor = ''}>
                      <McIcon materialKey={r.material} size={26} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: r.color }}>{r.name}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{r.rarity}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </StaggerItem>
        </div>
      </StaggerContainer>
    </div>
  );
}
